import orgLogo from '@/assets/images/layout/org-logo.png';
import { ROUTES } from '@/app/config/constants';
import type { SidebarMenuItem, SidebarOrg } from './types';

/*
  The screens say BID where they used to say RFQ.

  Labels only, on instruction: `feature:` names, permission strings, template kind codes, workflow document
  types and route paths all still read RFQ, because those are the platform's own names and renaming them
  would close every screen the feature guards. What a person reads changed; what the two sides agree on
  did not.
*/

export const SIDEBAR_WIDTH_EXPANDED = 240;
export const SIDEBAR_WIDTH_COLLAPSED = 68;

export function getSidebarWidth(isCollapsed: boolean): number {
  return isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
}

/**
 * 64px, not the 56px in niraxobid.md section 10.1: the comp's user chip is two
 * lines (14px/1.429 name over 12px/1.667 role = 40px) and the header carries
 * 24px of vertical padding, so 56px cannot hold it.
 */
export const HEADER_HEIGHT = 64;

export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebar_collapsed';

/** Static for now -- switching organizations needs a tenant API. */
export const DEFAULT_ORG: SidebarOrg = {
  id: 'ait-global',
  name: 'AIT Global',
  logoUrl: orgLogo,
};

/**
 * Mega Admin's sidebar, fixed in code rather than fetched.
 *
 * That role provisions tenants before any tenant menu exists, so /sidebar/menu
 * is not called for it at all (see useMenu). The shape matches what the API
 * would return, so SidebarMenu renders it through the same path -- no
 * `requiredRoles`, because the list is already role-specific.
 */
export const MEGA_ADMIN_MENU_ITEMS: readonly SidebarMenuItem[] = [
  {
    id: 'organization-setup',
    label: 'Organization Setup',
    icon: 'pi-building',
    path: ROUTES.ORGANIZATION_SETUP,
    order: 10,
  },
];

/**
 * The Templates submenu, each child naming the TEMPLATE feature the RBAC matrix scores it against.
 *
 * Its own constant so the catalogue reads the same way for both groups, the arrangement
 * MASTERS_MENU_CHILDREN already had. Which children a role actually sees is decided by that role's
 * grants, not by this list.
 *
 * The order is the reference's: `TEMPLATE_SUBS = ['RFQ Template', 'PR Template', 'Supplier Onboarding']`
 * in NiraxoBid_Template.html. The labels no longer are: the RFQ one reads BID Template, and on 2026-10-28
 * the other two were renamed to Purchase Request Template and Supplier Onboarding Template, here and in
 * PAGE_TITLES below, so the sidebar and the breadcrumb still name each screen the same way.
 * TEMPLATE_KIND_LABELS keeps the short names, and `feature` stays the `os_feature` name the guards and
 * grants resolve. `templateNavigation.test.ts` pins both lists.
 */
export const TEMPLATES_MENU_CHILDREN: readonly SidebarMenuItem[] = [
  {
    id: 'templates-rfq',
    label: 'BID Template',
    path: ROUTES.TEMPLATES_RFQ,
    order: 41,
    feature: 'RFQ Template',
  },
  {
    id: 'templates-pr',
    label: 'Purchase Request Template',
    path: ROUTES.TEMPLATES_PR,
    order: 42,
    feature: 'PR Template',
  },
  {
    id: 'templates-supplier-onboarding',
    label: 'Supplier Onboarding Template',
    path: ROUTES.TEMPLATES_SUPPLIER_ONBOARDING,
    order: 43,
    feature: 'Supplier Onboarding',
  },
];

/**
 * The Masters submenu, in the order every one of the nine design bundles lists
 * it (masters-category.html and its eight siblings carry identical sidebar
 * markup -- only which child is marked active differs).
 *
 * No `icon` on the children, because the reference renders them as plain
 * labels under the parent's rail; SidebarMenu omits the glyph when there is
 * none. `order` is explicit so sanitizeItems' sort preserves this sequence
 * rather than falling back to alphabetical.
 *
 * No `feature` either: these resolve through MASTER_DEFINITIONS by key, which
 * `withReadableMasters` already reads. That path works and is left alone.
 *
 * The Product entry, revisited: the nine older bundles split eight-to-one in
 * favour of "Material", and that outlier was read as a typo. `master.html`,
 * which supersedes them, lists `'Product'` in its `MASTERS` array and titles
 * the master `'Product'` with the prefix `MAT`. Eight older bundles do not
 * outweigh the current one, so the label is "Product". Only the folder and the
 * registry key remain `material`, which no user sees.
 */
export const MASTERS_MENU_CHILDREN: readonly SidebarMenuItem[] = [
  {
    id: 'masters-organization',
    label: 'Organization',
    path: ROUTES.MASTERS_ORGANIZATION,
    order: 71,
  },
  {
    id: 'masters-entity',
    label: 'Entity',
    path: ROUTES.MASTERS_ENTITY,
    order: 72,
  },
  {
    id: 'masters-business-unit',
    label: 'Business Unit',
    path: ROUTES.MASTERS_BUSINESS_UNIT,
    order: 73,
  },
  {
    id: 'masters-division',
    label: 'Division',
    path: ROUTES.MASTERS_DIVISION,
    order: 74,
  },
  {
    id: 'masters-department',
    label: 'Department',
    path: ROUTES.MASTERS_DEPARTMENT,
    order: 75,
  },
  {
    id: 'masters-location',
    label: 'Location',
    path: ROUTES.MASTERS_LOCATION,
    order: 76,
  },
  {
    id: 'masters-category',
    label: 'Category',
    path: ROUTES.MASTERS_CATEGORY,
    order: 77,
  },
  {
    id: 'masters-uom',
    label: 'Unit of Measurement',
    path: ROUTES.MASTERS_UOM,
    order: 78,
  },
  /*
    Country, State and City: hidden from the menu, not removed from the product.

    Review sheet #12 (2026-10-10): the three geography levels are seed data -- the platform ships India, its
    states and its major cities, and nobody maintains them through a screen. So they leave the Masters
    submenu for every role, and leave the RBAC screen with it (`UI_HIDDEN_MASTER_KEYS`, which
    `isMenuMaster` reads).

    Commented rather than deleted, and the rest of the three masters is untouched: the routes, the screens,
    the APIs, `MASTER_DEFINITIONS` and the `os_feature` rows all stand exactly as they were. Restoring the
    entries is uncommenting these eighteen lines and taking the three keys out of `UI_HIDDEN_MASTER_KEYS`.
    Orders 79-81 stay reserved for them, so the sequence either side is unchanged.
  */
  // {
  //   id: 'masters-country',
  //   label: 'Country',
  //   path: ROUTES.MASTERS_COUNTRY,
  //   order: 79,
  // },
  // {
  //   id: 'masters-state',
  //   label: 'State',
  //   path: ROUTES.MASTERS_STATE,
  //   order: 80,
  // },
  // {
  //   id: 'masters-city',
  //   label: 'City',
  //   path: ROUTES.MASTERS_CITY,
  //   order: 81,
  // },
  {
    id: 'masters-material',
    label: 'Product',
    path: ROUTES.MASTERS_MATERIAL,
    order: 82,
  },
  {
    id: 'masters-currency',
    label: 'Currency',
    path: ROUTES.MASTERS_CURRENCY,
    order: 83,
  },
  {
    id: 'masters-payment-terms',
    label: 'Payment Terms',
    path: ROUTES.MASTERS_PAYMENT_TERMS,
    order: 84,
  },
  {
    id: 'masters-delivery-terms',
    label: 'Delivery Terms',
    path: ROUTES.MASTERS_DELIVERY_TERMS,
    order: 85,
  },
  /*
    Not one of the design bundles' eighteen either: os_incoterm arrived with the change that made the
    Delivery Terms screen's Incoterm a reference instead of a typed code. Placed directly after Delivery
    Terms, which is the only master that cites it and the screen a user arrives here from.
  */
  {
    id: 'masters-incoterm',
    label: 'Incoterm',
    path: ROUTES.MASTERS_INCOTERM,
    order: 86,
  },
  {
    id: 'masters-pr',
    label: 'Purchase Request',
    /*
      The Purchase Requests screen itself (NB-355), and its only menu entry: shown to a role holding the screen's
      own grant, the one its routes are guarded by.
    */
    path: ROUTES.PURCHASE_REQUESTS,
    order: 87,
    feature: 'Purchase Requests',
  },
  {
    id: 'masters-supplier',
    label: 'Supplier',
    path: ROUTES.MASTERS_SUPPLIER,
    order: 88,
  },
  /*
    Not one of the design bundles' eighteen: os_designation was served long before it had a screen, and
    User Management's Designation field reads it. Placed beside User Management, whose records it files,
    so the list still ends where the bundles end it.
  */
  {
    id: 'masters-designation',
    label: 'Designation',
    path: ROUTES.MASTERS_DESIGNATION,
    order: 89,
  },
  {
    id: 'masters-user-management',
    label: 'User Management',
    path: ROUTES.MASTERS_USER_MANAGEMENT,
    order: 90,
  },
];

/**
 * Every menu the application has, in the order the design bundles list them.
 *
 * **One catalogue, not one list per role.** It used to be a list per role —
 * ADMIN_MENU_ITEMS and INITIATOR_MENU_ITEMS, chosen in `useMenu` by the role
 * SLUG. The slug vocabulary has three values (admin / initiator / supplier)
 * and the platform has ten roles and counting, so `Approver L1` mapped to no
 * slug, got no list, and fell through to a fetch of GET /sidebar/menu — an
 * endpoint the platform does not serve. Those roles signed in to an empty
 * sidebar, and every role an administrator creates from now on would have done
 * the same. A list per role is also the thing that cannot be dynamic: adding
 * `Buyer` would have meant adding an array.
 *
 * So this is the whole menu, and what a role SEES is decided entirely by that
 * role's grants — see `withReadableFeatures` and `withReadableMasters` in
 * `useMenu`. An entry governed by the RBAC matrix carries `feature`, naming
 * what the matrix scores it against. The Dashboard carries none, being
 * globally available; the Masters group carries none because its children
 * resolve through MASTER_DEFINITIONS; Templates carries none because its own
 * children name TEMPLATE features instead.
 *
 * GET /sidebar/menu is therefore never called. The endpoint is not served, and
 * a 403 from it is worse than a missing menu: the response interceptor turns
 * any 403 into a hard navigation to /unauthorized, which threw the whole
 * signed-in app out of the shell.
 *
 * MEGA_ADMIN_MENU_ITEMS stays separate and untouched: that role provisions
 * tenants before any of this applies and its shell is fixed in code.
 *
 * No `requiredRoles` on any entry — the grants decide, not the slug. (The one exception, Default Template
 * Forms, was removed on 2026-10-16.)
 */
export const APP_MENU_ITEMS: readonly SidebarMenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'pi-th-large',
    path: ROUTES.DASHBOARD,
    order: 10,
  },
  {
    id: 'rfqs',
    label: 'BIDs',
    icon: 'pi-file',
    path: ROUTES.RFQS,
    order: 20,
    feature: 'Request For Quotation',
  },
  /*
    BID Invitations, the supplier's side of BIDs: removed from the menu and from RBAC (2026-10-16). Its feature,
    Supplier Bid, was retired (V20261016130000, Inactive); the screen, routes and APIs stand. Restoring it is
    uncommenting the entry and setting Supplier Bid Active. Order 21 stays reserved.
  */
  // {
  //   id: 'supplier-bids',
  //   label: 'BID Invitations',
  //   icon: 'pi-inbox',
  //   path: ROUTES.SUPPLIER_BIDS,
  //   order: 21,
  //   feature: 'Supplier Bid',
  // },
  {
    id: 'reverse-auction',
    label: 'Reverse Auction',
    icon: 'pi-sync',
    path: ROUTES.REVERSE_AUCTION,
    order: 35,
    feature: 'Reverse Auction',
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: 'pi-clone',
    path: ROUTES.TEMPLATES,
    order: 40,
    children: [...TEMPLATES_MENU_CHILDREN],
  },
  {
    id: 'rbac',
    label: 'RBAC',
    icon: 'pi-shield',
    path: ROUTES.RBAC,
    order: 50,
    feature: 'RBAC',
  },
  {
    id: 'workflow-config',
    label: 'Workflow Configuration',
    icon: 'pi-sitemap',
    path: ROUTES.WORKFLOW_CONFIG,
    order: 60,
    feature: 'Workflow Configuration',
  },
  {
    id: 'ai-control',
    label: 'AI Control',
    icon: 'pi-sliders-h',
    path: ROUTES.AI_CONTROL,
    order: 65,
    feature: 'AI Control',
  },
  {
    id: 'master',
    label: 'Master',
    icon: 'pi-database',
    path: ROUTES.MASTERS,
    order: 70,
    children: [...MASTERS_MENU_CHILDREN],
  },
  /*
    No Organization Setup entry, although it is a FUNCTION feature the matrix
    scores. It belongs to the Mega Admin who provisions the tenant, and
    MEGA_ADMIN_MENU_ITEMS is its only home. Keeping it out of the catalogue is
    what leaves the wizard's APIs unrequested for every other role -- the
    screen is never opened, and RoleShellGuard turns away a typed URL.
  */
];

/** Five dots, exactly as the comp has it. */
export const SEARCH_PLACEHOLDER = 'Search anything.....';

export const PAGE_TITLES: Record<string, string> = {
  [ROUTES.DASHBOARD]: 'Dashboard',
  [ROUTES.PROFILE]: 'Profile',
  [ROUTES.SETTINGS]: 'Settings',
  [ROUTES.RFQS]: 'BIDs',
  /* A record's own pages (/rfqs/12, /rfqs/12/edit) fall back to 'BIDs' by longest prefix. */
  [ROUTES.RFQS_TEMPLATES]: 'Active Templates',
  [ROUTES.RFQS_NEW]: 'New BID',
  /* A bid (/supplier-bids/12) falls back to 'BID Invitations' by longest prefix. */
  [ROUTES.SUPPLIER_BIDS]: 'BID Invitations',
  /* The Supplier Dashboard (2026-10-29, NB-637), for a user whose role is Supplier. */
  [ROUTES.SUPPLIER_DASHBOARD]: 'Dashboard',
  [ROUTES.PURCHASE_REQUESTS]: 'Purchase Requests',
  [ROUTES.REVERSE_AUCTION]: 'Reverse Auction',
  /*
    The breadcrumb reads `Templates / <submenu>`, and the submenu names match the
    menu exactly (TEMPLATES_MENU_CHILDREN), including the 2026-10-28 names
    Purchase Request Template and Supplier Onboarding Template. The screen's
    own heading is a separate string in the
    reference and stays as it is -- 'RFQ Templates', 'Purchase Request Templates',
    'Supplier Onboarding Templates'. The menu names one thing to go to; the
    heading names what is on the screen.
  */
  [ROUTES.TEMPLATES]: 'Templates',
  [ROUTES.TEMPLATES_PR]: 'Purchase Request Template',
  [ROUTES.TEMPLATES_RFQ]: 'BID Template',
  [ROUTES.TEMPLATES_SUPPLIER_ONBOARDING]: 'Supplier Onboarding Template',
  [ROUTES.RBAC]: 'RBAC',
  [ROUTES.WORKFLOW_CONFIG]: 'Workflow Config',
  [ROUTES.AI_CONTROL]: 'AI Control',
  [ROUTES.MASTERS]: 'Master',
  /*
    One per Masters child. Without these the header would title-case the URL
    segment -- "Business Unit" would read "Business-unit", and "Uom" would
    never become "Unit of Measurement".
  */
  [ROUTES.MASTERS_ORGANIZATION]: 'Organization',
  [ROUTES.MASTERS_ENTITY]: 'Entity',
  [ROUTES.MASTERS_BUSINESS_UNIT]: 'Business Unit',
  [ROUTES.MASTERS_DIVISION]: 'Division',
  [ROUTES.MASTERS_DEPARTMENT]: 'Department',
  [ROUTES.MASTERS_LOCATION]: 'Location',
  [ROUTES.MASTERS_CATEGORY]: 'Category',
  [ROUTES.MASTERS_UOM]: 'Unit of Measurement',
  [ROUTES.MASTERS_COUNTRY]: 'Country',
  [ROUTES.MASTERS_STATE]: 'State',
  [ROUTES.MASTERS_CITY]: 'City',
  /* The artboard titles this master Product; only the folder says material. */
  [ROUTES.MASTERS_MATERIAL]: 'Product',
  [ROUTES.MASTERS_CURRENCY]: 'Currency',
  [ROUTES.MASTERS_PAYMENT_TERMS]: 'Payment Terms',
  [ROUTES.MASTERS_DELIVERY_TERMS]: 'Delivery Terms',
  [ROUTES.MASTERS_INCOTERM]: 'Incoterm',
  [ROUTES.MASTERS_PR]: 'Purchase Requests',
  [ROUTES.MASTERS_SUPPLIER]: 'Suppliers',
  [ROUTES.MASTERS_DESIGNATION]: 'Designation',
  [ROUTES.MASTERS_USER_MANAGEMENT]: 'User Management',

  [ROUTES.ORGANIZATION_SETUP]: 'Organization Setup',
  // Without this the header would title-case the URL segment to "Completed".
  [`${ROUTES.ORGANIZATION_SETUP}/completed`]: 'Organization Setup',
};
